import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Phone, FileText } from 'lucide-react';
import axios from 'axios';
import EmbeddedStripePaymentElement from '../components/EmbeddedStripePaymentElement';
import './v2.css';

const LOGO = "/westex-logo.png";
const PHONE = "8064011749";

/* ============== ANIMATED NEON DIVIDER ============== */
export const NeonDivider = ({ delay = 0 }) => (
  <div className="v2-neon-divider" aria-hidden="true" style={{ animationDelay: `${delay}s` }}>
    <span className="v2-neon-line" />
  </div>
);

/* ============== SOLID HERO DIVIDER (non-breathing) ============== */
export const HeroDivider = () => (
  <div className="v2-hero-divider" aria-hidden="true" />
);

/* ============== SCROLL PROGRESS ============== */
export const ScrollProgress = () => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const on = () => {
      const h = document.documentElement;
      setP(h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight));
    };
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  return <div className="v2-progress" style={{ transform: `scaleX(${p})` }} />;
};

/* ============== MOUSE-FOLLOW SPOTLIGHT ============== */
export const Spotlight = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.left = e.clientX + 'px';
        el.style.top = e.clientY + 'px';
        el.classList.add('active');
      });
    };
    const onLeave = () => el.classList.remove('active');
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);
  return <div className="v2-spotlight" ref={ref} aria-hidden />;
};

/* ============== COUNT UP ============== */
export const CountUp = ({ to, duration = 1800 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const start = () => {
      if (done.current) return;
      done.current = true;
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        const e = 1 - Math.pow(1 - p, 3);
        setVal(Math.floor(e * to));
        if (p < 1) requestAnimationFrame(step);
        else setVal(to);
      };
      requestAnimationFrame(step);
    };
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) { start(); return; }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) start(); });
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{val.toLocaleString()}</span>;
};

/* ============== FADE IN V1 SECTIONS ============== */
export const useFadeInSections = () => {
  useEffect(() => {
    const root = document.querySelector('.v2-content');
    if (!root) return;
    const candidates = root.querySelectorAll('section, .gradient-outline, .gradient-outline-white');
    candidates.forEach(el => el.classList.add('v2-fade-in'));
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    candidates.forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-visible');
      else obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);
};

/* ============== BUTTON ============== */
export const V2Button = ({ children, to, href, onClick, variant = 'primary', testId }) => {
  const cls = `v2-btn v2-btn-${variant}`;
  const content = (
    <>
      <span>{children}</span>
      <span className="v2-btn-arrow">→</span>
    </>
  );
  if (to) return <Link to={to} className={cls} data-testid={testId} onClick={onClick}>{content}</Link>;
  if (href) return <a href={href} className={cls} data-testid={testId} onClick={onClick}>{content}</a>;
  return <button className={cls} data-testid={testId} onClick={onClick}>{content}</button>;
};

/* ============== PAYMENT MODAL ============== */
export const PaymentModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [refId, setRefId] = useState('');
  const [amount, setAmount] = useState('');
  const [step, setStep] = useState('form'); // 'form' | 'pay'
  const [err, setErr] = useState('');

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setRefId(''); setAmount(''); setStep('form'); setErr('');
    }
  }, [open]);

  const validateAndProceed = () => {
    if (!refId.trim()) return setErr('Reference ID required');
    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) return setErr('Invalid amount');
    if (num < 10) return setErr('Minimum $10.00');
    if (num > 2000) return setErr('Maximum $2,000.00');
    setErr('');
    setStep('pay');
  };

  const createIntent = useCallback(async () => {
    const res = await axios.post('/api/payments/checkout/intent', {
      reference_id: refId.trim(),
      amount: parseFloat(amount),
      origin_url: window.location.origin,
    }, { timeout: 30000 });
    return res.data;
  }, [refId, amount]);

  return (
    <div
      className={`v2-modal-backdrop ${open ? 'visible' : ''}`}
      onClick={step === 'form' ? onClose : undefined}
    >
      <div
        className="v2-modal"
        onClick={(e) => e.stopPropagation()}
        style={step === 'pay' ? { maxWidth: 540, padding: '32px 24px' } : undefined}
      >
        <button className="v2-modal-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="v2-modal-tag">Secure Payment</div>
        <h3>Pay Invoice</h3>
        <p className="v2-modal-sub">
          {step === 'form'
            ? 'Reference your job number and balance due.'
            : `Paying $${parseFloat(amount).toFixed(2)} for Job ${refId}.`}
        </p>

        {step === 'form' && (
          <>
            <div className="v2-modal-field">
              <label>Reference No. / Job No.</label>
              <input type="text" value={refId} onChange={e => setRefId(e.target.value)} placeholder="12345" data-testid="v2-pay-ref" />
            </div>
            <div className="v2-modal-field">
              <label>Amount USD</label>
              <input type="text" value={amount} onChange={e => { if (/^\d*\.?\d*$/.test(e.target.value)) setAmount(e.target.value); }} placeholder="0.00" data-testid="v2-pay-amount" />
            </div>
            {err && <div className="v2-modal-error">{err}</div>}
            <div className="v2-modal-actions">
              <V2Button onClick={onClose} variant="ghost">Cancel</V2Button>
              <V2Button onClick={validateAndProceed} testId="v2-pay-submit">Continue to Payment</V2Button>
            </div>
          </>
        )}

        {step === 'pay' && (
          <>
            <div style={{ marginTop: 16, marginBottom: 12, background: 'var(--pv2-ink, #0a0a0a)', border: '1px solid #292929' }}>
              <EmbeddedStripePaymentElement
                createIntent={createIntent}
                returnUrl={`${window.location.origin}/payment-success`}
                onSuccess={(pi) => {
                  onClose();
                  navigate(`/payment-success?payment_intent_id=${pi?.id || ''}`);
                }}
              />
            </div>
            <div className="v2-modal-actions" style={{ marginTop: 12 }}>
              <V2Button onClick={() => setStep('form')} variant="ghost">← Back</V2Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ============== NAVBAR ============== */
export const V2NavBar = () => {
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);
  const cls = ({ isActive }) => `v2-nav-link ${isActive ? 'active' : ''}`;
  const links = [
    { to: '/', end: true, num: '01', label: 'Home' },
    { to: '/about', num: '02', label: 'About' },
    { to: '/services', num: '03', label: 'Services' },
    { to: '/areas-pricing', num: '04', label: 'Coverage' },
    { to: '/notary', num: '05', label: 'Notary' },
    { to: '/faq', num: '06', label: 'FAQ' },
    { to: '/contact', num: '07', label: 'Contact' },
  ];
  return (
    <>
      <header className="v2-nav" data-testid="v2-navbar">
        <Link to="/" className="v2-nav-brand" data-testid="v2-nav-brand" aria-label="WesTex Legal — Home">
          <img src={LOGO} alt="WesTex Legal" className="v2-nav-brand-logo" />
        </Link>
        <div className="v2-nav-inner">
          <nav className="v2-nav-links">
            {links.map(l => (
              <NavLink key={l.to} to={l.to} end={l.end} className={cls} data-num={l.num}>
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>
        {/* Actions live OUTSIDE nav-inner — anchored to the full-width header so they don't shift the centered links */}
        <div className="v2-nav-actions">
          <button className="v2-nav-pay" onClick={() => setPayOpen(true)} data-testid="v2-nav-pay">
            Pay Invoice
          </button>
          <Link to="/portal/login" className="v2-nav-portal" data-testid="v2-nav-portal">Portal</Link>
          <a href={`tel:${PHONE}`} className="v2-nav-cta" data-testid="v2-nav-call">
            <span>(806) 401-1749</span>
          </a>
          <button className="v2-nav-mobile-toggle" onClick={() => setOpen(v => !v)} aria-label="Menu">☰</button>
        </div>
        {open && (
          <div className="v2-nav-mobile">
            {links.map(l => (
              <NavLink key={l.to} to={l.to} end={l.end} className={cls}>
                <span style={{ color: 'hsl(357, 90%, 62%)', marginRight: 10, fontSize: 10 }}>{l.num}</span>
                {l.label}
              </NavLink>
            ))}
            <Link to="/portal/login">Client Portal</Link>
            <button onClick={() => setPayOpen(true)}>Pay Invoice</button>
            <a href={`tel:${PHONE}`}>(806) 401-1749</a>
          </div>
        )}
      </header>
      <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} />
    </>
  );
};

/* ============== FOOTER ============== */
export const V2Footer = () => (
  <footer className="v2-footer" data-testid="v2-footer">
    <div className="v2-footer-inner">
      <div className="v2-footer-top">
        <div className="v2-footer-logo-block">
          <img src={LOGO} alt="WesTex Legal" className="v2-footer-logo" />
          <p className="v2-footer-blurb">
            Professional process serving across all 254 Texas counties, all of New Mexico, and nationwide. NAPPS-certified. Direct-line communication. Rigorous documentation.
          </p>
        </div>
        <div>
          <h4>Company</h4>
          <ul>
            <li><Link to="/about">About</Link></li>
            <li><Link to="/services">Services</Link></li>
            <li><Link to="/areas-pricing">Coverage</Link></li>
            <li><Link to="/faq">FAQ</Link></li>
          </ul>
        </div>
        <div>
          <h4>Quick Links</h4>
          <ul>
            <li><Link to="/job-request">Submit a Job</Link></li>
            <li><Link to="/notary">Notary</Link></li>
            <li><Link to="/portal/login">Client Portal</Link></li>
            <li><Link to="/contact">Contact</Link></li>
          </ul>
        </div>
        <div>
          <h4>Reach Us</h4>
          <ul>
            <li><a href={`tel:${PHONE}`}>(806) 401-1749</a></li>
            <li><a href="mailto:WesTexLegal@gmail.com">WesTexLegal@gmail.com</a></li>
          </ul>
        </div>
      </div>
      <div className="v2-footer-bottom">
        <span>© 2016–{new Date().getFullYear()} WesTex Legal · All Rights Reserved</span>
        <span className="v2-footer-legal-links">
          <Link to="/privacy" data-testid="v2-footer-privacy">Privacy</Link>
          <span aria-hidden="true" style={{ margin: '0 8px', opacity: 0.4 }}>·</span>
          <Link to="/terms" data-testid="v2-footer-terms">Terms</Link>
        </span>
      </div>
    </div>
  </footer>
);

/* ============== MOBILE STICKY CTA BAR ============== */
export const V2MobileCTABar = () => {
  const location = useLocation();
  const onJobRequest = location.pathname === '/job-request';
  return (
    <div className="v2-mobile-cta" data-testid="v2-mobile-cta">
      <a href={`tel:${PHONE}`} className="v2-mobile-cta-call" data-testid="v2-mobile-cta-call">
        <Phone size={16} strokeWidth={2.2} />
        <span>Call Now</span>
      </a>
      {!onJobRequest && (
        <Link to="/job-request" className="v2-mobile-cta-submit" data-testid="v2-mobile-cta-submit">
          <FileText size={16} strokeWidth={2.2} />
          <span>Submit a Job</span>
        </Link>
      )}
    </div>
  );
};

/* ============== V2 LAYOUT ============== */
export const V2Layout = ({ children }) => {
  const location = useLocation();
  useEffect(() => { window.scrollTo({ top: 0 }); }, [location.pathname]);
  return (
    <div className="v2-root" data-testid="v2-root">
      <ScrollProgress />
      <Spotlight />
      <V2NavBar />
      {children}
      <V2Footer />
      <V2MobileCTABar />
    </div>
  );
};

/* ============== PAGE HEADER (inner pages) ============== */
export const PageHeader = ({ caseNum, crumb, title, accent, sub }) => (
  <section className="v2-page-header">
    <div className="v2-page-header-inner">
      <div className="v2-page-crumbs">
        <Link to="/">WesTex Legal</Link>
        <span className="sep">/</span>
        <span className="current">{crumb}</span>
        {caseNum && <>
          <span className="sep">·</span>
          <span>{caseNum}</span>
        </>}
      </div>
      <h1 className="v2-page-title">
        {title} {accent && <em>{accent}</em>}
      </h1>
      {sub && <p className="v2-page-sub">{sub}</p>}
    </div>
  </section>
);

/* ============== V1 WRAP ============== */
export const V1Wrap = ({ children }) => {
  useFadeInSections();
  return <div className="v2-content">{children}</div>;
};
